import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { SidePanelSummary } from 'components/SidePanel/SidePanelSummary'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { Application, CustomerApplicationDetails, CustomerUseCase } from 'types/customers'
import { statusLabelMap } from '../utils/utils'
import { CustomerOnboardingTimeline } from './CustomerOnboardingTimeline'

interface ApplicationSidePanelBodyProps {
  application: Application | undefined
  data: CustomerApplicationDetails | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
  isIndividual?: boolean
  isReliance?: boolean
  hasUploadedDocuments?: boolean
}

interface ApplicationDetailsSectionProps {
  reference: string | undefined
  id: string | undefined
  type: string | undefined
  model: string | undefined
  status: Application['status'] | undefined
  email: string | undefined
}

const ApplicationDetailsSection = ({
  reference,
  id,
  type,
  model,
  status,
  email,
}: ApplicationDetailsSectionProps) => {
  const { t } = useTranslation()
  return (
    <SidePanelSection heading={t('customers.applicationDetails')}>
      <SidePanelSummary
        rows={[
          {
            label: t('customers.externalReference'),
            content: ellipseMiddleText(reference || id || '', 12, 8),
            ...((reference || id) && { copyText: reference || id }),
          },
          { label: t('customers.customerType'), content: type || '' },
          { label: t('customers.onboardingType'), content: model || '' },
          { label: t('status'), content: (status && statusLabelMap[status]) || status || '' },
          { label: t('customers.emailAddress'), content: email || '' },
        ]}
      />
    </SidePanelSection>
  )
}

export const CustomerSidePanelBody = ({
  application,
  data,
  isLoading,
  isError,
  refetch,
  isIndividual,
  isReliance,
  hasUploadedDocuments,
}: ApplicationSidePanelBodyProps) => {
  if (isLoading) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (isError) {
    return (
      <Box paddingX={16} paddingY={32}>
        <RequestErrorStatusCard onReload={refetch} />
      </Box>
    )
  }

  const { reference, id, status, type, model, useCases } = application || {}
  const isV1Customer = !application?.authenticatedLink?.link
  const isSelfCustody = useCases?.includes(CustomerUseCase.SELF_CUSTODY)
  const email = isSelfCustody
    ? data?.individual?.emailAddress || data?.users?.[0]?.email || undefined
    : undefined

  return (
    <Box flexCol gapY={16} padding={16}>
      <CustomerOnboardingTimeline
        application={application}
        data={data}
        isV1Customer={isV1Customer}
        isIndividual={isIndividual}
        isReliance={isReliance}
        hasUploadedDocuments={hasUploadedDocuments}
      />
      <ApplicationDetailsSection
        reference={reference}
        id={id}
        type={type}
        model={model}
        status={status}
        email={email}
      />
    </Box>
  )
}
